import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/authContext';
import ProfileImage from '../../profileImage';

const RequestCard = ({ request, onCancel, onAccept, onReject }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'approved': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'cancelled': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'expired': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRequestTypeColor = (requestType) => {
    switch (requestType) {
      case 'championship': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'tournament': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'league': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'friendly': return 'bg-green-100 text-green-800 border-green-200';
      case 'single_match': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getUrgencyColor = (daysUntilMatch) => {
    if (daysUntilMatch <= 3) return 'text-red-600 bg-red-50';
    if (daysUntilMatch <= 7) return 'text-orange-600 bg-orange-50';
    return 'text-green-600 bg-green-50';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((date - now) / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInDays > 30) {
      return `${Math.floor(diffInDays / 30)} month${Math.floor(diffInDays / 30) > 1 ? 's' : ''} away`;
    } else if (diffInDays > 0) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} away`;
    } else if (diffInHours > 0) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} away`;
    } else {
      return 'Very soon';
    }
  };

  const handleViewRequest = () => {
    navigate(`/organization/competition-requests/${request._id}`);
  };

  // Get current user's organization ID based on account type
  const getCurrentUserOrganizationId = () => {
    if (!user) return null;
    
    if (user.accountType === 'organization') {
      return user._id || user.id;
    } else if (user.accountType === 'supervisor') {
      return user.metaData?.organization;
    }
    
    return null;
  };

  const currentUserOrgId = getCurrentUserOrganizationId();
  const isSentRequest = currentUserOrgId && request.senderOrganizationId?._id === currentUserOrgId;
  
  const otherOrganization = isSentRequest ? 
    request.receiverOrganizationId?.organizationName || request.receiverOrganizationId?.firstName || 
    request.receiverOrganizationId?.fullName :
    request.senderOrganizationId?.organizationName || request.senderOrganizationId?.firstName ||
    request.senderOrganizationId?.fullName;
  
  const otherTeam = isSentRequest ? request.receiverTeamId : request.senderTeamId;
  const ownTeam = isSentRequest ? request.senderTeamId : request.receiverTeamId;

  const canAccept = !isSentRequest && request.status === 'pending';
  const canReject = !isSentRequest && request.status === 'pending';

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
      {/* Enhanced Header with Status Strip */}
      <div className={`h-1 w-full ${request.status === 'pending' ? 'bg-gradient-to-r from-amber-400 to-orange-400' : 
        request.status === 'approved' ? 'bg-gradient-to-r from-emerald-400 to-green-400' : 
        'bg-gradient-to-r from-gray-400 to-gray-500'}`}></div>
      
      <div className="p-6">
        {/* Header Section */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-3">
              <h3 className="text-xl font-bold text-gray-900 line-clamp-1">{request.title}</h3>
              <div className="flex space-x-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(request.status)}`}>
                  {request.status.toUpperCase()}
                </span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getRequestTypeColor(request.requestType)}`}>
                  {request.requestType?.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            </div>
            
            <div className="text-sm text-gray-600 mb-2">
              <span className="font-medium">{isSentRequest ? 'Sent to:' : 'Received from:'}</span> {otherOrganization}
            </div>
          </div>

          {/* Request Direction Badge */}
          <div className="text-xs text-gray-500">
            {isSentRequest ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Outgoing
              </span>
            ) : (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                </svg>
                Incoming
              </span>
            )}
          </div>
        </div>

        {/* Teams Matchup Section */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-center space-x-4">
            {/* Own Team */}
            <div className="flex-1 text-center">
              <div className="flex items-center justify-center space-x-3">
                <div className="w-12 h-12 rounded-full bg-white shadow-sm border-2 border-gray-200 flex items-center justify-center overflow-hidden">
                  <ProfileImage
                    image={ownTeam?.logo}
                    alt={ownTeam?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900 text-sm">{ownTeam?.name}</p>
                  <p className="text-xs text-gray-500">@{ownTeam?.uniqueName}</p>
                  <p className="text-xs text-gray-400">{ownTeam?.memberCount || 0} members</p>
                </div>
              </div>
            </div>
            
            {/* VS Divider */}
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-xs">VS</span>
              </div>
            </div>
            
            {/* Other Team */}
            <div className="flex-1 text-center">
              <div className="flex items-center justify-center space-x-3">
                <div className="text-right">
                  <p className="font-semibold text-gray-900 text-sm">{otherTeam?.name}</p>
                  <p className="text-xs text-gray-500">@{otherTeam?.uniqueName}</p>
                  <p className="text-xs text-gray-400">{otherTeam?.memberCount || 0} members</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-white shadow-sm border-2 border-gray-200 flex items-center justify-center overflow-hidden">
                  <ProfileImage
                    image={otherTeam?.logo}
                    alt={otherTeam?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="mb-4">
          <p className="text-gray-700 line-clamp-2 leading-relaxed">{request.description}</p>
        </div>

        {/* Enhanced Match Details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          {/* Proposed Match Date */}
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
            <div className="flex items-center space-x-2 mb-1">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm font-semibold text-blue-800">Match Date</p>
            </div>
            <p className="text-sm text-blue-700 font-medium">{formatDate(request.proposedMatchDate)}</p>
            <p className={`text-xs font-medium mt-1 px-2 py-1 rounded-full inline-block ${getUrgencyColor(request.daysUntilMatch)}`}>
              {formatRelativeTime(request.proposedMatchDate)}
            </p>
          </div>

          {/* Request Age */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="flex items-center space-x-2 mb-1">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-semibold text-gray-700">Request Age</p>
            </div>
            <p className="text-sm text-gray-600">{formatDate(request.createdAt)}</p>
            <p className="text-xs text-gray-500 mt-1">
              {request.ageInDays === 0 ? 'Today' : `${request.ageInDays} day${request.ageInDays > 1 ? 's' : ''} ago`}
            </p>
          </div>

          {/* Created By */}
          <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
            <div className="flex items-center space-x-2 mb-1">
              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <p className="text-sm font-semibold text-purple-800">Created By</p>
            </div>
            <p className="text-sm text-purple-700 font-medium">{request.createdBy?.fullName}</p>
            <p className="text-xs text-purple-600">@{request.createdBy?.userName}</p>
          </div>
        </div>

        {/* Message Section */}
        {request.message && (
          <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-2 mb-2">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-sm font-semibold text-blue-800">Additional Message</p>
            </div>
            <p className="text-sm text-blue-700 leading-relaxed italic">"{request.message}"</p>
          </div>
        )}

        {/* Enhanced Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="flex flex-wrap gap-2">
            {/* Always show View Details */}
            <button
              onClick={handleViewRequest}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors duration-200 shadow-sm"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View Details
            </button>

            {/* Role-specific actions */}
            {isSentRequest ? (
              // Sender actions
              request.status === 'pending' && (
                <button
                  onClick={() => onCancel(request._id)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 transition-colors duration-200 shadow-sm"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel Request
                </button>
              )
            ) : (
              // Receiver actions
              <>
                {canAccept && (
                  <button
                    onClick={() => onAccept(request._id)}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-lg hover:bg-emerald-700 transition-colors duration-200 shadow-sm"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Accept Request
                  </button>
                )}
                {canReject && (
                  <button
                    onClick={() => onReject(request._id)}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 transition-colors duration-200 shadow-sm"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Reject Request
                  </button>
                )}
              </>
            )}
          </div>

          {/* Priority/Urgency Indicator */}
          {request.status === 'pending' && request.daysUntilMatch <= 7 && (
            <div className="flex items-center space-x-1">
              <svg className="w-4 h-4 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-xs font-medium text-orange-700">
                {request.daysUntilMatch <= 3 ? 'URGENT' : 'SOON'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestCard; 